import { lazy, Suspense } from "react";
import { Navigate, Route, Routes as RouterRoutes } from "react-router-dom";

import Loader from "../components/Common/Loader";
import ProtectedRoute from "../components/ProtectedRoute";
import PublicRoute from "../components/PublicRoute";
import { ROUTES } from "./routePaths";

// Lazy-loaded route containers
const SignInContainer = lazy(
  () => import("../containers/SignInContainer/SignInContainer"),
);
const PasswordLostContainer = lazy(
  () => import("../containers/PasswordLostContainer/PasswordLostContainer"),
);
const PasswordResetContainer = lazy(
  () => import("../containers/NewPasswordContainer/NewPasswordView"),
);
const AcceptInviteContainer = lazy(
  () => import("../containers/AcceptInviteContainer/AcceptInviteContainer"),
);
const HomeContainer = lazy(
  () => import("../containers/HomeContainer/HomeContainer"),
);
const PostingDetailsContainer = lazy(
  () => import("../containers/PostingContainer/PostingDetailContainer"),
);
const TicketListContainer = lazy(
  () => import("../containers/TicketListContainer/TicketListScreen"),
);
const TicketFormContainer = lazy(
  () => import("../containers/TicketFormContainer/TicketFormContainer"),
);
const TicketDetailContainer = lazy(
  () => import("../containers/TicketDetailContainer/TicketDetailContainer"),
);
const DocumentContainer = lazy(
  () => import("../containers/DocumentListContainer/DocumentListContainer"),
);
const PropertyManagementContainer = lazy(
  () =>
    import(
      "../containers/PropertyManagementContainer/PropertyManagementContainer"
    ),
);
const ProfileContainer = lazy(
  () => import("../containers/ProfileContainer/ProfileContainer"),
);
const AccountContainer = lazy(
  () => import("../containers/ProfileContainer/AccountContainer"),
);
const PrivacyContainer = lazy(
  () => import("../containers/ProfileContainer/PrivacyContainer"),
);
const TermsOfUseContainer = lazy(
  () => import("../containers/ProfileContainer/TermsOfUseContainer"),
);
const MyDataContainer = lazy(
  () => import("../containers/ProfileContainer/MyDataContainer"),
);
const DataPrivacyContainer = lazy(
  () => import("../containers/ProfileContainer/DataPrivacyContainer"),
);
const ImprintContainer = lazy(
  () => import("../containers/ProfileContainer/ImprintContainer"),
);
const ContactContainer = lazy(
  () => import("../containers/ProfileContainer/ContactContainer"),
);
const TandcContainer = lazy(
  () => import("../containers/ProfileContainer/TandcContainer"),
);
const UpdatePasswordContainer = lazy(
  () => import("../containers/UpdatePasswordContainer/UpdatePasswordContainer"),
);

const Routes = () => {
  return (
    <Suspense fallback={<Loader />}>
      <RouterRoutes>
        {/* Sign-in */}
        <Route
          path={ROUTES.SIGN_IN}
          element={
            <PublicRoute>
              <SignInContainer />
            </PublicRoute>
          }
        />

        {/* Password Lost */}
        <Route
          path={ROUTES.PASSWORD_LOST}
          element={
            <PublicRoute>
              <PasswordLostContainer />
            </PublicRoute>
          }
        />

        {/* Password Reset */}
        <Route
          path={ROUTES.NEW_PASSWORD}
          element={
            <PublicRoute>
              <PasswordResetContainer />
            </PublicRoute>
          }
        />

        {/* Accept Invitation */}
        <Route
          path={ROUTES.ACCEPT_INVITE}
          element={
            <PublicRoute>
              <AcceptInviteContainer />
            </PublicRoute>
          }
        />

        {/*
          Protected Area ->
        */}

        {/* Home "/" */}
        <Route
          path={ROUTES.HOME}
          element={
            <ProtectedRoute>
              <HomeContainer />
            </ProtectedRoute>
          }
        />

        {/* Posting detail "/postings/:postingId" */}
        <Route
          path={ROUTES.POSTING_DETAIL}
          element={
            <ProtectedRoute>
              <PostingDetailsContainer />
            </ProtectedRoute>
          }
        />

        {/* Ticket "/tickets" */}
        <Route
          path={ROUTES.TICKET_LIST}
          element={
            <ProtectedRoute>
              <TicketListContainer />
            </ProtectedRoute>
          }
        />
        {/* Ticket add "/tickets/new" */}
        <Route
          path={ROUTES.TICKET_NEW}
          element={
            <ProtectedRoute>
              <TicketFormContainer />
            </ProtectedRoute>
          }
        />
        {/* Ticket slave "/tickets/:ticketId" */}
        <Route
          path={ROUTES.TICKET_DETAIL}
          element={
            <ProtectedRoute>
              <TicketDetailContainer />
            </ProtectedRoute>
          }
        />

        {/* Documents "/documents" */}
        <Route
          path={ROUTES.DOCUMENTS}
          element={
            <ProtectedRoute>
              <DocumentContainer />
            </ProtectedRoute>
          }
        />

        {/* Property Management  */}
        <Route
          path={ROUTES.PROPERTY_MANAGEMENT}
          element={
            <ProtectedRoute>
              <PropertyManagementContainer />
            </ProtectedRoute>
          }
        />

        {/* Profile "/profile" */}
        <Route
          path={ROUTES.PROFILE}
          element={
            <ProtectedRoute>
              <ProfileContainer />
            </ProtectedRoute>
          }
        />
        {/* Profile "/profile/account" */}
        <Route
          path={ROUTES.PROFILE_ACCOUNT}
          element={
            <ProtectedRoute>
              <AccountContainer />
            </ProtectedRoute>
          }
        />
        {/* Profile "/profile/privacy" */}
        <Route
          path={ROUTES.PROFILE_PRIVACY}
          element={
            <ProtectedRoute>
              <PrivacyContainer />
            </ProtectedRoute>
          }
        />
        {/* Profile "/profile/terms-of-use" */}
        <Route
          path={ROUTES.PROFILE_TERMS_OF_USE}
          element={
            <ProtectedRoute>
              <TermsOfUseContainer />
            </ProtectedRoute>
          }
        />
        {/* Profile "/profile/my-data" */}
        <Route
          path={ROUTES.PROFILE_MY_DATA}
          element={
            <ProtectedRoute>
              <MyDataContainer />
            </ProtectedRoute>
          }
        />
        {/* Profile "/profile/data-privacy" */}
        <Route
          path={ROUTES.PROFILE_DATA_PRIVACY}
          element={
            <ProtectedRoute>
              <DataPrivacyContainer />
            </ProtectedRoute>
          }
        />
        {/* Profile "/profile/imprint" */}
        <Route
          path={ROUTES.PROFILE_IMPRINT}
          element={
            <ProtectedRoute>
              <ImprintContainer />
            </ProtectedRoute>
          }
        />
        {/* Profile "/profile/contact" */}
        <Route
          path={ROUTES.PROFILE_CONTACT}
          element={
            <ProtectedRoute>
              <ContactContainer />
            </ProtectedRoute>
          }
        />
        {/* Profile "/profile/tandc" */}
        <Route
          path={ROUTES.PROFILE_TANDC}
          element={
            <ProtectedRoute>
              <TandcContainer />
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.UPDATE_PASSWORD}
          element={
            <ProtectedRoute>
              <UpdatePasswordContainer />
            </ProtectedRoute>
          }
        />

        {/* default route - redirect to home */}
        <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
      </RouterRoutes>
    </Suspense>
  );
};

export default Routes;
